import { useEffect, useState } from 'react'
import { createContainer } from 'unstated-next'
import moment from 'moment'
import * as api from './../../infrastructure/api'
import { useTableEditionMode } from '../common/customComponents'
import { MarketSalesFilters, MktSaleMovement, MktSaleChart, emptyChart, MovementStatus, MktSales, MktSaleDate, MktSaleShipTosFilters, MktSaleForecastShipToDetails, MktSalesSum } from './models'
import { SapCounterparty } from '../deals/dealModels'
import { dealApi } from '../deals/dealApis'
import { compareAndFixPeriod } from '../../infrastructure/date'
import { MktSalesFiltersContainer } from './mktSalesFiltersStore'
import { interval, Subject } from 'rxjs'
import { debounce, filter, map, switchMap } from 'rxjs/operators'
import { snackbars } from '../../infrastructure/snackbars'
import { t } from '../../infrastructure/i18nextHelper'

type MktSalesQueryRxProps = { filters: MarketSalesFilters }

function useMktSales() {
    let tableEditMode = useTableEditionMode<MktSaleMovement>()
    let filterStore = MktSalesFiltersContainer.useContainer()
    let [mktSalesQuerySubject] = useState(new Subject<MktSalesQueryRxProps>())

    let [showForecastPopup, setShowForecastPopup] = useState<boolean>(false)
    let [mktSaleDates, setMktSaleDates] = useState<MktSaleDate[]>([])
    let [unchangedMktSaleMovements, setUnchangedMktSaleMovements] = useState<MktSaleMovement[]>([])
    let [chart, setChart] = useState<MktSaleChart>(emptyChart)
    let [unit, setUnit] = useState<string | undefined>()
    let [displayMode, setDisplayMode] = useState<'split' | 'graph' | 'table'>('split')
    let [sapCounterpartys, setSapCounterpartys] = useState<SapCounterparty[]>([])
    let [sum, setSum] = useState<MktSalesSum | undefined>(undefined)

    useEffect(() => {
        let selectedProducts = filterStore.productIdsFilter.values()
        let units = filterStore.productIdsFilter.items.filter(x => selectedProducts.includes(x.id)).map(x => x.unit).distinct()
        setUnit(units?.length == 1 ? units[0] : undefined)
    }, [filterStore.productIdsFilter.selectedChoices, filterStore.productIdsFilter.items])

    function subscribeMktSalesQuery() {
        let areFiltersReady = ({ filters }: MktSalesQueryRxProps) =>
            !!filters.startDate && !!filters.endDate && filters.useSiteInPercentageCompute !== undefined
            && (!!filters.productId && filters.productId.length > 0)
            && (!!filters.companies && filters.companies.length > 0)
            && (!!filters.site && filters.site.length > 0)
            && (!!filters.customerSegments && filters.customerSegments.length > 0)

        let mktSalesQuerySubscription = mktSalesQuerySubject.asObservable()
            .pipe(
                filter(areFiltersReady),
                map(constructFiltersQuery),
                debounce(() => interval(1)),
                switchMap(async ({ query, useSiteInPercentageCompute }) => {
                    try { return { result: await api.get<MktSales>(query), useSiteInPercentageCompute } } catch { return { result: Promise.resolve(undefined), useSiteInPercentageCompute } }
                }),
                filter(x => x.result !== undefined),
                map(x => x as { result: MktSales, useSiteInPercentageCompute: boolean })
            )
            .subscribe(({ result, useSiteInPercentageCompute }) => setMktSaleMovements(result, useSiteInPercentageCompute))

        return mktSalesQuerySubscription
    }

    let constructFiltersQuery = ({ filters }: MktSalesQueryRxProps) => {
        let query = 'stock/movement/mktsale?'

        query = query + `startDate=${filters.startDate}&`
        query = query + `endDate=${filters.endDate}&`
        query = query + `useSiteInPercentageCompute=${filters.useSiteInPercentageCompute}&`
        filters.companies!.map(x => { query = query + `companies=${x}&` })
        filters.customerSegments!.map(x => { query = query + `customerSegments=${x}&` })
        filters.site!.map(x => { query = query + `site=${x}&` })
        filters.productId!.map(x => { query = query + `productId=${x}&` })

        return { query, useSiteInPercentageCompute: filters.useSiteInPercentageCompute }
    }

    let setMktSaleMovements = async (mktSales: MktSales, useSiteInPercentageCompute: boolean) => {
        setChart(mktSales.chart)
        changeMktSaleMovements(mktSales.movementItems, useSiteInPercentageCompute)
        setUnchangedMktSaleMovements(JSON.parse(JSON.stringify(mktSales.movementItems))) // deep copy
        setSum(mktSales.sum)
        if (mktSales.truncated)
            snackbars.warning(t('mktSales.label.warning.truncated'))
    }

    let loadMktSaleMovements = async () => {
        mktSalesQuerySubject.next({
            filters: {
                startDate: filterStore.startDate,
                endDate: filterStore.endDate,
                site: filterStore.sitesFilter.values(),
                customerSegments: filterStore.customerSegmentsFilter.values(),
                companies: filterStore.companiesFilter.values(),
                productId: filterStore.productIdsFilter.values(),
                useSiteInPercentageCompute: filterStore.useSiteInPercentageCompute
            }
        })
    }

    let constructShipTosFiltersQuery = (filters: MktSaleShipTosFilters) => {
        let query = ''
        let filterProperties = Object.keys(filters)
        let initializedFilters = filterProperties.filter(x => filters[x] != null && x === 'date')

        initializedFilters.map(x => query = query + `dateFrom=${filters[x]}&dateTo=${filters[x]}&`)
        filters.companys?.map(x => { query = query + `companys=${x}&` })
        filters.customerSegments?.map(x => { query = query + `customerSegments=${x}&` })
        filters.sites?.map(x => { query = query + `sites=${x}&` })
        filters.productIds?.map(x => { query = query + `productIds=${x}&` })

        return query
    }

    let closeForecastPopup = async () => {
        setShowForecastPopup(false)
        await loadMktSaleMovements()
    }

    let createMktSaleDates = (movements: MktSaleMovement[], useSiteInPercentageCompute: boolean): MktSaleDate[] => {
        let mktSaleDatesIndex = mktSaleDates.indexBy(x => x.date)

        let add = (x, acc) => (acc ?? 0) + (x ?? 0)

        return movements
            .groupBy(x => x.date, x => x)
            .map(([key, items]) => {
                let distinctApStatuses = items.map(x => x.accountingPeriodStatus).distinct()
                let forecastQuantity = items.map(x => x.forecastQuantity).reduce(add);
                let siteQuantity = items.map(x => x.siteQuantity).reduce(add);
                let sapQuantity = items.map(x => x.sapQuantity).reduce(add);
                let result = {
                    date: key,
                    isOpen: mktSaleDatesIndex[key]?.isOpen ?? false,
                    forecastQuantity: forecastQuantity,
                    plannedQuantity: items.map(x => x.plannedQuantity).reduce(add),
                    siteQuantity: siteQuantity,
                    accountingPeriodStatus: distinctApStatuses.length == 1 ? distinctApStatuses.first() : null,
                    sapQuantity: sapQuantity,
                    quantity: items.map(x => x.quantity).reduce(add),
                    initialVolume: items.map(x => x.initialVolume).reduce(add),
                    longTermPlanVolume: items.map(x => x.longTermPlanVolume).reduce(add),
                    status: getMktSalesStatus(items),
                    movements: items,
                    percentage: computePercentage(forecastQuantity, useSiteInPercentageCompute ? siteQuantity : sapQuantity)
                }
                return result
            })
    }

    let getMktSalesStatus = (items: MktSaleMovement[]): MovementStatus => {
        if (items.every(x => x.status === MovementStatus.Actual))
            return MovementStatus.Actual

        if (items.some(x => x.status === MovementStatus.Forecast))
            return MovementStatus.Forecast

        if (items.some(x => x.status === MovementStatus.Planned))
            return MovementStatus.Planned

        return MovementStatus.Forecast
    }

    let changeMktSaleMovements = (movememts: MktSaleMovement[], useSiteInPercentageCompute: boolean) => setMktSaleDates(createMktSaleDates(movememts, useSiteInPercentageCompute))

    let allMktSaleDatesSelected = mktSaleDates.filter(x => x.isOpen).length == mktSaleDates.length

    let toggleAllMktSaleDate = () => {
        let isOpen = !allMktSaleDatesSelected
        for (let x of mktSaleDates) {
            x.isOpen = isOpen
        }
        setMktSaleDates([...mktSaleDates])
    }

    let toggleMktSaleDate = (date: string) => {
        let mktSaleDate = mktSaleDates.find(x => x.date == date)
        if (mktSaleDate)
            mktSaleDate.isOpen = !mktSaleDate.isOpen
        setMktSaleDates([...mktSaleDates])
    }

    let toggleTableEditMode = () => {
        tableEditMode.setIsOn(!tableEditMode.isOn)
    }

    let getShipTos = async (mktSale: MktSaleDate, movementIndex?: number | null) => {
        let mktSaleMovement = movementIndex !== null ? mktSale.movements[movementIndex!] : null

        let filters: MktSaleShipTosFilters = {
            companys: (movementIndex !== null
                ? [filterStore.companiesFilter.items.find(c => c.code == mktSaleMovement!.company)!.code]
                : filterStore.companiesFilter.values()) ?? [],
            sites: (movementIndex !== null
                ? [filterStore.sitesFilter.items.find(s => s.code == mktSaleMovement!.site)!.code]
                : filterStore.sitesFilter.values() ?? []),
            date: mktSale.date,
            productIds: (movementIndex !== null
                ? [filterStore.productIdsFilter.items.find(p => p.id === mktSaleMovement!.productId)!.id.toString()]
                : filterStore.productIdsFilter.values()) ?? [],
            customerSegments: (movementIndex !== null
                ? [mktSaleMovement!.customerSegment!]
                : filterStore.customerSegmentsFilter.values()) ?? [],
        }

        let query = constructShipTosFiltersQuery(filters)
        let mktSaleShipTos = await api.get<MktSaleForecastShipToDetails[]>(`stock/movement/mktsale/shipTo?${query}`)
        return mktSaleShipTos
    }

    let loadSapCounterPartys = async () => {
        let dealSapCounterpartys = await dealApi.getSapCounterpartys()
        setSapCounterpartys(dealSapCounterpartys)
    }

    let changePeriod = (startDate: string | null, endDate: string | null) => {
        let period = compareAndFixPeriod(
            filterStore.startDate ?? null,
            startDate,
            filterStore.endDate ?? null,
            endDate
        )
        filterStore.setStartDate(period.start)
        filterStore.setEndDate(period.end)
    }

    let onFullMonth = () => {
        let start = moment(filterStore.startDate)
        let end = moment(filterStore.endDate)

        return start.month() == end.month() &&
            start.isSame(moment(start).startOf('month'), 'day') &&
            end.isSame(moment(end).endOf('month'), 'day')
    }

    let computePercentage = (forecastQuantity: number | null, actualQuantity: number | null) => {
        if (forecastQuantity == null || actualQuantity == null || forecastQuantity === 0) return null
        return Math.round(actualQuantity / forecastQuantity * 100)
    }

    return {
        loadMktSaleMovements, showForecastPopup, setShowForecastPopup,
        unchangedMktSaleMovements, setUnchangedMktSaleMovements,
        mktSaleDates, changeMktSaleMovements,
        allMktSaleDatesSelected, toggleAllMktSaleDate, toggleMktSaleDate,
        chart, setChart, unit, closeForecastPopup,
        toggleTableEditMode, tableEditMode,
        displayMode, setDisplayMode, getShipTos, sapCounterpartys, loadSapCounterPartys,
        changePeriod, onFullMonth, subscribeMktSalesQuery,
        computePercentage, sum
    }
}

export let MktSalesContainer = createContainer(useMktSales)